import { config } from "../Environment";

// ─── Types ───────────────────────────────────────────────────────────────────

export type InsightType =
  | "streak_milestone"
  | "completion_milestone"
  | "idle_nudge"
  | "progress_summary"
  | "pattern_detected"
  | "outcome_tracking"
  | "weekly_summary"
  | "monthly_summary";

export type InsightStatus =
  | "generated"
  | "delivered"
  | "seen"
  | "dismissed"
  | "acted_on";

export type InsightPriority = "low" | "medium" | "high";

export interface SuggestedAction {
  label: string;
  actionUrl?: string;
  actionType?: string;
}

export interface ProactiveInsight {
  _id: string;
  fiUser: string;
  insightType: InsightType;
  title: string;
  message: string;
  richContent?: string;
  priority: InsightPriority;
  status: InsightStatus;
  source?: {
    analysisType?: "realtime" | "nearrealtime" | "batch";
    triggeredBy?: string;
  };
  linkedEntities?: {
    fiProcess?: string;
    fiPrescription?: string;
    fiSpace?: string;
  };
  suggestedActions: SuggestedAction[];
  userReaction?: "helpful" | "not_helpful" | null;
  userReactionFeedback?: string;
  generatedAt: number;
  deliveredAt?: number;
  seenAt?: number;
  dismissedAt?: number;
}

export interface ProactiveInsightListResponse {
  insights: ProactiveInsight[];
  total: number;
}

export interface UnreadCountResponse {
  count: number;
}

export interface EngagementMetrics {
  totalProcesses: number;
  activeProcesses: number;
  completedProcesses: number;
  totalInsights: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate?: number;
}

// ─── API Functions ───────────────────────────────────────────────────────────

export const listProactiveInsights = async (
  retryFetch: Function,
  limit?: number,
  offset?: number,
  insightType?: InsightType
): Promise<ProactiveInsightListResponse> => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", String(limit));
  if (offset) params.append("offset", String(offset));
  if (insightType) params.append("insightType", insightType);

  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/proactive${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch proactive insights");
  }

  return response.json();
};

export const getUnreadInsightCount = async (
  retryFetch: Function
): Promise<UnreadCountResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/proactive/unread-count`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch unread insight count");
  }

  return response.json();
};

export const markInsightSeen = async (
  retryFetch: Function,
  id: string
): Promise<void> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/proactive/${id}/seen`,
    {
      method: "PUT",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to mark insight as seen");
  }
};

export const dismissInsight = async (
  retryFetch: Function,
  id: string
): Promise<void> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/proactive/${id}/dismiss`,
    {
      method: "PUT",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to dismiss insight");
  }
};

export const setInsightReaction = async (
  retryFetch: Function,
  id: string,
  reaction: "helpful" | "not_helpful" | null,
  feedback?: string
): Promise<void> => {
  const body: Record<string, unknown> = { reaction };
  if (feedback !== undefined) {
    body.feedback = feedback;
  }

  const response = await retryFetch(
    `${config.API_BASE}/api/insights/proactive/${id}/reaction`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to set insight reaction");
  }
};

export const getEngagementMetrics = async (
  retryFetch: Function
): Promise<EngagementMetrics> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/insights/metrics`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch engagement metrics");
  }

  const data = await response.json();
  // Backend returns nested structure under data.metrics with sub-objects
  // (completion, workspace, streaks, idle). Map to flat frontend type.
  const m = data.metrics || data;

  return {
    totalProcesses: m.completion?.totalCompleted ?? m.totalProcesses ?? 0,
    activeProcesses: m.activeProcesses ?? 0,
    completedProcesses: m.completion?.totalCompleted ?? m.completedProcesses ?? 0,
    totalInsights: m.workspace?.totalInsights ?? m.totalInsights ?? 0,
    currentStreak: m.streaks?.currentStreak ?? m.currentStreak ?? 0,
    longestStreak: m.streaks?.longestStreak ?? m.longestStreak ?? 0,
    lastActivityDate: m.idle?.lastActivityDate ?? m.lastActivityDate,
  };
};
